// src/pages/workflow/Home.tsx
import React from "react";
import {
  Box,
  Container,
  Typography,
  Button,
  GridLegacy as Grid,
  Card,
  CardContent,
  CardMedia,
  Stack,
  Avatar,
  Chip,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import {
  CleaningServices,
  Kitchen,
  Restaurant,
  AccessTime,
  CheckCircle,
  Phone,
  ArrowForward,
  Star,
  Groups,
  Shield,
  Gradient,
  People,
} from "@mui/icons-material";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";

export default function WorkflowHome() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { t } = useTranslation();
  const fHomeT = (key: string) => t(`workflowhome:${key}`);

  const services = [

    {
      icon: <Restaurant sx={{ fontSize: 40 }} />,
      title: fHomeT('kitchenHelper'),
      description: fHomeT("kitchenHelperDescription"),
      color: "#09203F",
    },
    {
      icon: <Kitchen sx={{ fontSize: 40 }} />,
      title: fHomeT('dishwashingServices'),
      description: fHomeT("dishwashingServicesDescription"),
      color: "#2196f3",
    },
    // {
    //   icon: <People sx={{ fontSize: 40 }} />,
    //   title: fHomeT("waiterSupport"),
    //   description: fHomeT("waiterSupportDescription"),
    //   color: "#C33764",
    // },
    {
      icon: <CleaningServices sx={{ fontSize: 40 }} />,
      title: fHomeT("commercialCleaning"),
      description: fHomeT("commercialCleaningDescription"),
      color: "#4caf50",
    },
  ];

  const stats = [
    { value: "100%", label: fHomeT("happyClients") },
    { value: "24/7", label: fHomeT("supportAvailable") },
    { value: "45 min", label: fHomeT("avgResponseTime") },

  ];

  return (
    <Box sx={{ overflow: "hidden" }}>
      {/* Hero Section */}
      <Box
        sx={{
          background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
          color: "white",
          pt: { xs: 8, md: 12 },
          pb: { xs: 8, md: 15 },
          position: "relative",
        }}
      >
        <Container maxWidth="lg">
          <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
              <Chip
                label="Trusted Service Provider"
                icon={<Shield />}
                sx={{
                  backgroundColor: "rgba(255,255,255,0.2)",
                  color: "white",
                  mb: 3,
                }}
              />
              <Typography
                variant="h1"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: "2.5rem", md: "3.5rem" },
                  lineHeight: 1.2,
                  mb: 3,
                }}
              >
                {fHomeT('feature')}{" "}
                <Box component="span" sx={{ color: "#ffeb3b" }}>
                  On-Demand
                </Box>
              </Typography>
              <Typography
                variant="h5"
                sx={{ mb: 4, opacity: 0.9, fontWeight: 400 }}
              >
                {fHomeT("heroSubtitle")}
              </Typography>
              <Stack direction={{ xs: "column", sm: "row" }} spacing={2}>
                <Button
                  variant="contained"
                  size="large"
                  component={Link}
                  to="/workflow/booking"
                  endIcon={<ArrowForward />}
                  sx={{
                    backgroundColor: "#ff6b35",
                    fontSize: "1.1rem",
                    py: 1.5,
                    px: 4,
                    "&:hover": { backgroundColor: "#e55a2b" },
                  }}
                >
                  {fHomeT("bookNow")}
                </Button>
                <Button
                  variant="outlined"
                  size="large"
                  component={Link}
                  to="/workflow/services"
                  sx={{
                    borderColor: "white",
                    color: "white",
                    fontSize: "1.1rem",
                    py: 1.5,
                    px: 4,
                    "&:hover": { borderColor: "#ffeb3b", color: "#ffeb3b" },
                  }}
                >
                  {fHomeT("viewServices")}
                </Button>
              </Stack>
            </Grid>
            <Grid item xs={12} md={6}>
              <Box
                component="img"
                src="../logos/kitchen.jpeg"
                alt="Professional kitchen staff"
                sx={{
                  width: "100%",
                  borderRadius: 4,
                  boxShadow: "0 20px 60px rgba(0,0,0,0.3)",
                }}
              />
            </Grid>
          </Grid>
        </Container>
      </Box>


      {/* Services Preview */}
      <Container sx={{ py: { xs: 8, md: 12 } }}>
        <Typography
          variant="h2"
          textAlign="center"
          sx={{ fontWeight: 800, mb: 2 }}
        >
          {fHomeT("ourServices")}
        </Typography>
        <Typography
          variant="h5"
          textAlign="center"
          color="text.secondary"
          sx={{ mb: 8, maxWidth: 700, mx: "auto" }}
        >
          {fHomeT("ourServicesSubtitle")}
        </Typography>
        <Box sx={{
          columnCount: { xs: 1, sm: 3 },
          columnGap: 6,
          mb: 4
        }}>
          {services.map((service, index) => (
            <Box
              key={index}
              sx={{
                breakInside: 'avoid',
                mb: 4,
                display: 'flex',
                flexDirection: 'column',
                pb: 3,
                borderBottom: '1px solid',
                borderColor: 'divider',
                pageBreakInside: 'avoid',
                WebkitColumnBreakInside: 'avoid'
              }}
            >
              <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mb: 2, background: 'linear-gradient(105deg, #93A5CF , #E4EfE9)', borderRadius: 2, p: 1 }}>
                <Box
                  sx={{
                    width: 60,
                    height: 60,
                    borderRadius: '50%',
                    backgroundColor: `${service.color}15`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    mr: 2,
                    flexShrink: 0
                  }}
                >
                  <Box sx={{ color: service.color, fontSize: 24 }}>
                    {service.icon}
                  </Box>
                </Box>
                <Typography variant="h5" sx={{ fontWeight: 600, color: service.color }}>
                  {service.title}
                </Typography>
              </Box>
              <Typography color="text.secondary" sx={{ mb: 2 }}>
                {service.description}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                <Chip
                  label="Flexible Hours"
                  size="small"
                  sx={{
                    backgroundColor: `${service.color}15`,
                    color: service.color,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label="Trained Staff"
                  size="small"
                  sx={{
                    backgroundColor: `${service.color}15`,
                    color: service.color,
                    fontSize: '0.75rem'
                  }}
                />
                <Chip
                  label="Quick Booking"
                  size="small"
                  sx={{
                    backgroundColor: `${service.color}15`,
                    color: service.color,
                    fontSize: '0.75rem'
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      </Container>


      {/* Stats Section */}
      <Container sx={{ py: 6, mt: -5 }}>
        <Grid container spacing={1}>
          {stats.map((stat, index) => (
            <Grid item xs={6} md={3} key={index}>
              <Card
                sx={{
                  textAlign: "center",
                  py: 3,
                  borderRadius: 3,
                  boxShadow: "0 8px 32px rgba(0,0,0,0.08)",
                  border: "1px solid #e0e0e0",
                }}
              >
                <Typography
                  variant="h3"
                  sx={{ fontWeight: 800, color: theme.palette.primary.main }}
                >
                  {stat.value}
                </Typography>
                <Typography color="text.secondary">{stat.label}</Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Container>

      {/* How It Works */}
      <Box sx={{ backgroundColor: "#f8f9fa", py: { xs: 8, md: 12 } }}>
        <Container maxWidth="lg">
          <Typography
            variant="h2"
            textAlign="center"
            sx={{ fontWeight: 800, mb: 2 }}
          >
            {fHomeT("howitWorks")}
          </Typography>
          <Typography
            variant="h5"
            textAlign="center"
            color="text.secondary"
            sx={{ mb: 8, maxWidth: 700, mx: "auto" }}
          >
            {fHomeT("simple4stepProcess")}
          </Typography>
          <Grid container spacing={4}>
            {[
              {
                step: "1",
                title: fHomeT("bookService"),
                description: fHomeT("selectServiceDescription"),
              },
              {
                step: "2",
                title: fHomeT("matchAndConfirm"),
                description: fHomeT("matchAndConfirmDescription"),
              },
              {
                step: "3",
                title: fHomeT("serviceDelivery"),
                description: fHomeT("serviceDeliveryDescription"),
              },
              {
                step: "4",
                title: fHomeT("rateAndReview"),
                description: fHomeT("rateAndReviewDescription"),
              },
            ].map((item, index) => (
              <Grid item xs={12} md={3} key={index}>
                <Box sx={{ textAlign: "center" }}>
                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      backgroundColor: theme.palette.primary.main,
                      color: "white",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: "2rem",
                      fontWeight: 800,
                      mx: "auto",
                      mb: 3,
                    }}
                  >
                    {item.step}
                  </Box>
                  <Typography variant="h5" sx={{ mb: 2, fontWeight: 700 }}>
                    {item.title}
                  </Typography>
                  <Typography color="text.secondary">{item.description}</Typography>
                </Box>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* CTA Section */}
      <Container sx={{ py: { xs: 8, md: 12 }, textAlign: "center" }}>
        <Box
          sx={{
            background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
            borderRadius: 4,
            p: { xs: 4, md: 8 },
            color: "white",
          }}
        >
          <Typography variant="h2" sx={{ fontWeight: 800, mb: 3 }}>
            {fHomeT("ctaTitle")}
          </Typography>
          <Typography variant="h5" sx={{ mb: 5, opacity: 0.9 }}>
            {fHomeT("ctaDescription")}
          </Typography>
          <Button
            variant="contained"
            size="large"
            component={Link}
            to="/booking"
            sx={{
              backgroundColor: "#ff6b35",
              fontSize: "1.1rem",
              py: 1.5,
              px: 6,
              "&:hover": { backgroundColor: "#e55a2b" },
            }}
          >
            {fHomeT("startButton")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}